import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LoadingIcon } from "../../assets/icons";
import {
    getPointApprovalRequest,
    pointsReversal,
    pointsDebiting,
    rejectPointApprovalRequest,
} from "../../service/query/auth";

export default function ReviewPointsAdjustment() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [membershipIdInput, setMembershipIdInput] = useState("");
    const [showRejectReason, setShowRejectReason] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const fetchRequest = async () => {
        setLoading(true);
        try {
            const res = await getPointApprovalRequest(id);
            setRequest(res?.data?.data ?? null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleApprove = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const actionFn = request.action === "debiting" ? pointsDebiting : pointsReversal;
            await actionFn({
                payment_ref: request.payment_ref,
                request_id: request.id,
            });
            navigate("/points-approvals");
        } catch (err) {
            setError(err?.response?.data?.message ?? "Failed to approve request.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!showRejectReason) {
            setShowRejectReason(true);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await rejectPointApprovalRequest(id, {
                reason: rejectReason,
                membership_id: membershipIdInput,
            });
            navigate("/points-approvals");
        } catch (err) {
            setError(err?.response?.data?.message ?? "Failed to reject request.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 bg-white min-h-[80vh] flex justify-center items-center">
                <LoadingIcon />
            </div>
        );
    }

    if (!request) {
        return (
            <div className="p-6 bg-white min-h-[80vh]">
                <p className="text-gray-400">Request not found.</p>
            </div>
        );
    }

    const isCredit = request.action === "credit";
    const points = Number(request.points ?? request.adjustment ?? 0);

    return (
        <div className="p-6 bg-white min-h-[80vh]">
            <button
                onClick={() => navigate("/points-approvals")}
                className="text-sm text-[#667085] mb-2 flex items-center gap-1"
            >
                ← Back to pending requests
            </button>
            <h3 className="text-[20px] font-semibold text-[#212121] mb-6">Review Points Adjustment</h3>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                {/* Adjustment details */}
                <div className="border border-[#EAECF0] rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-[#212121]">Adjustment details</h4>
                        <span className="bg-[#FEF6E7] text-[#B45309] px-3 py-1 rounded-full text-[12px] font-medium">
                            {request.status}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-y-5 text-sm">
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Customer Membership ID</p>
                            <p className="text-[#212121] font-medium">{request.customer?.membership_id ?? request.membership_id}</p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Current balance</p>
                            <p className="text-[#212121] font-medium">
                                {request.current_balance != null ? `${Number(request.current_balance).toLocaleString()} points` : "-"}
                            </p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Action</p>
                            <p className="text-[#212121] font-medium capitalize">{request.action}</p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Adjustment</p>
                            <p className={`font-medium ${isCredit ? "text-[#0E9F6E]" : "text-[#E02424]"}`}>
                                {isCredit ? "+" : "-"}{points.toLocaleString()} points
                            </p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Balance after approval</p>
                            <p className="text-[#212121] font-medium">
                                {request.balance_after_approval != null
                                    ? `${Number(request.balance_after_approval).toLocaleString()} points`
                                    : "-"}
                            </p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Payment reference</p>
                            <p className="text-[#212121] font-medium">{request.payment_ref}</p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Maker</p>
                            <p className="text-[#212121] font-medium">
                                {request.maker?.First_name} {request.maker?.Last_name}
                            </p>
                        </div>
                        <div>
                            <p className="text-[#667085] text-[12px] mb-1">Submitted</p>
                            <p className="text-[#212121] font-medium">{new Date(request.created_at).toLocaleString()}</p>
                        </div>
                    </div>

                    <hr className="my-5" />
                    <p className="text-[#667085] text-[12px] mb-1">Maker's reason</p>
                    <p className="text-[#212121] text-sm">{request.reason}</p>
                </div>

                {/* Decision panel */}
                <div className="border border-[#EAECF0] rounded-xl p-6 h-fit">
                    <h4 className="font-semibold text-[#212121] mb-4">Your decision</h4>

                    <div className="bg-[#F8FAFC] rounded-lg p-3 text-[13px] text-[#667085] mb-4">
                        {isCredit
                            ? "Approving this request will immediately credit the customer's points balance."
                            : "Approving this request will apply the requested points adjustment to the customer's balance."}
                    </div>

                    <label className="block text-[12px] font-medium text-black mb-2">Membership ID</label>
                    <input
                        type="text"
                        value={membershipIdInput}
                        onChange={(e) => setMembershipIdInput(e.target.value)}
                        placeholder="Membership ID"
                        className="w-full px-3 py-2 border border-[#CED4DF] rounded-[5px] h-[40px] text-sm mb-4 focus:outline-none focus:ring-1 focus:ring-blue-900"
                    />

                    {showRejectReason && (
                        <div className="mb-4">
                            <label className="block text-[12px] font-medium text-black mb-2">Rejection reason</label>
                            <textarea
                                rows={3}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason..."
                                className="w-full px-3 py-2 border border-[#CED4DF] rounded-[5px] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-900"
                            />
                        </div>
                    )}

                    <button
                        onClick={handleApprove}
                        disabled={submitting}
                        className="w-full bg-[#00305E] hover:bg-blue-800 text-white py-2 rounded text-sm font-medium mb-3 flex items-center justify-center h-[40px]"
                    >
                        {submitting ? <LoadingIcon /> : "Approve adjustment"}
                    </button>

                    <button
                        onClick={handleReject}
                        disabled={submitting || (showRejectReason && !rejectReason)}
                        className="w-full bg-[#FA2323] hover:bg-red-600 text-white py-2 rounded text-sm font-medium mb-3 flex items-center justify-center h-[40px] disabled:opacity-50"
                    >
                        {submitting ? <LoadingIcon /> : showRejectReason ? "Confirm rejection" : "Reject request"}
                    </button>

                    <button
                        onClick={() => navigate("/points-approvals")}
                        className="w-full border border-[#222222] text-[#000000] py-2 rounded text-sm hover:bg-gray-100 h-[40px]"
                    >
                        Cancel
                    </button>

                    {error && <p className="text-[#E02424] text-sm mt-3">{error}</p>}

                    <p className="text-[11px] text-[#98A2B3] mt-4">
                        Your name, decision, note and timestamp will be recorded in the audit log.
                    </p>
                </div>
            </div>
        </div>
    );
}